import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewContainerRef,
} from "@angular/core";

import { Subscription } from "rxjs";

import { CurrencyService } from "@hermes/aphrodite/price";
import type { QuickBuyProductInfos } from "@hermes/fragments/quick-buy-modal";
import { AddToCartFacade, QUICK_BUY } from "@hermes/states/add-to-cart";
import { BasketFacade } from "@hermes/states/basket";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";

import { QuickBuyModalManagerService } from "../../services/quick-buy-modal-manager.service";

@Component({
    selector: "h-quick-buy-button",
    templateUrl: "./quick-buy-button.component.html",
    styleUrls: ["quick-buy-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickBuyButtonComponent implements OnInit, OnDestroy {
    @Input()
    public id!: string;

    @Input()
    public quickBuyProductInfos!: QuickBuyProductInfos;

    @Input()
    public parentElementRefOfQuickBuyButton?: ElementRef<HTMLDivElement>;

    @Input()
    public describedBy?: string;

    @Input()
    public isCallToActionButton: boolean = false;

    @Input()
    public isGridContext: boolean = true;

    public canAddToCart: boolean = true;
    public isLoading: boolean = false;
    public addingToCart: boolean = false;

    private subscription: Subscription = new Subscription();

    constructor(
        private changeDetector: ChangeDetectorRef,
        private addToCartFacade: AddToCartFacade,
        private basketFacade: BasketFacade,
        private currencyService: CurrencyService,
        private quickBuyModalManager: QuickBuyModalManagerService,
        private viewContainerReference: ViewContainerRef,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.addToCartFacade.isAddToCartLoading$.subscribe((isLoading) => {
                if (isLoading) {
                    this.canAddToCart = false;
                    if (this.addingToCart) {
                        this.isLoading = true;
                    }
                } else {
                    this.canAddToCart = true;
                    this.addingToCart = false;
                    this.isLoading = false;
                }

                this.changeDetector.detectChanges();
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Launch quick buy modal if product has variations or just add to cart.
     * @param event
     */
    public quickBuyAction(event: Event): void {
        stopEventPropagation(event);
        if (
            this.parentElementRefOfQuickBuyButton &&
            this.quickBuyProductInfos.variants
        ) {
            this.quickBuyModalManager.openQuickBuyModal(
                this.quickBuyProductInfos,
                this.parentElementRefOfQuickBuyButton,
                this.viewContainerReference,
                this.isGridContext,
            );
        } else {
            this.quickBuyModalManager.closeQuickBuyModal();
            this.addToCart();
        }
    }

    /**
     * QuickBuy modal is watching mouseUp event, so we need to stop its propagation too.
     * @param event
     */
    public stopEventPropagation(event: Event) {
        stopEventPropagation(event);
    }

    public addToCart(): void {
        const product = this.quickBuyProductInfos.product;
        if (this.canAddToCart) {
            this.addingToCart = true;
            this.changeDetector.detectChanges();

            this.basketFacade.addToCart({
                sku: product.sku,
                displayNotifOnSuccess: true,
                displayNotifOnFail: true,
                itemPosition: 0,
                templateType: product.templateType,
                isQuickBuy: true,
                sendAnalyticsEvent: true,
                context: QUICK_BUY,
                product,
                currencyCode: this.currencyService.getCurrencyMap()?.[1],
            });
        }
    }
}
